import { useEffect, useRef, useState } from 'react'
import { getDocumentStoragePolicy, reserveUploadBatch, uploadToPresignedPost, completeUploadSession, getUploadSession, getDocumentNode, cancelUploadSession } from 'services/documents'
import type { DocumentNode, ReservedUploadSession } from 'types/documents'
import type { MailDocumentDestination } from 'components/mail/MailDocumentPicker'
type Status = 'queued' | 'reserving' | 'uploading' | 'completing' | 'verify' | 'failed' | 'completed' | 'cancelled'
export interface MailLocalUpload { id: string; file: File; destination: MailDocumentDestination; key: string; status: Status; progress: number; reservation?: ReservedUploadSession; uploaded: boolean; error?: string; nodeId?: number; removed: boolean; terminal?: boolean }
interface Options { sessionId: string; selectedCount: number; onComplete: (node: DocumentNode) => void }
export function useMailLocalUploads(options: Options) {
  const latest = useRef(options); latest.current = options
  const operations = useRef(new Map<string, MailLocalUpload>()), controllers = useRef(new Map<string, AbortController>())
  const generation = useRef(0), running = useRef(new Set<string>()), pump = useRef<() => void>(() => {})
  const [items, setItems] = useState<MailLocalUpload[]>([]), [notice, setNotice] = useState('')
  const publish = () => setItems([...operations.current.values()].filter(item => !item.removed).map(item => ({ ...item })))
  const patch = (op: MailLocalUpload, value: Partial<MailLocalUpload>, context: number) => { if (context === generation.current) { Object.assign(op, value); publish() } }
  useEffect(() => {
    generation.current++; operations.current.clear(); running.current.clear(); setItems([]); setNotice('')
    return () => {
      generation.current++
      controllers.current.forEach(controller => controller.abort()); controllers.current.clear()
      operations.current.forEach(op => { if (op.reservation && !['completed', 'cancelled'].includes(op.status)) void cancelUploadSession(op.reservation.upload_id).catch(() => {}) })
    }
  }, [options.sessionId])
  const run = async (op: MailLocalUpload, context: number) => {
    const controller = new AbortController(); controllers.current.set(op.id, controller)
    const current = () => context === generation.current && !op.removed && !controller.signal.aborted
    const accept = (node: DocumentNode) => {
      if (!current()) return
      patch(op, { nodeId: node.id, status: 'completed', progress: 100, error: undefined }, context)
      latest.current.onComplete(node)
    }
    try {
      if (!op.reservation) {
        patch(op, { status: 'reserving', error: undefined }, context)
        const policy = await getDocumentStoragePolicy()
        if (!current()) return
        if (op.file.size > policy.max_file_bytes || op.file.size > policy.max_batch_bytes) throw new Error('Il file supera il limite documentale consentito.')
        if (op.file.size > policy.available_bytes) throw new Error('Quota documentale insufficiente.')
        const [reservation] = await reserveUploadBatch({ spaceId: op.destination.spaceId, parentId: op.destination.parentId, idempotencyKey: op.key, files: [{ client_id: op.id, name: op.file.name, size_bytes: op.file.size, content_type: op.file.type }] })
        if (!current()) { if (context === generation.current) await cancelUploadSession(reservation.upload_id).catch(() => {}); return }
        op.reservation = reservation
      }
      if (op.uploaded || op.status === 'verify') {
        patch(op, { status: 'verify' }, context)
        const state = await getUploadSession(op.reservation.upload_id)
        if (!current()) return
        if (state.status === 'COMPLETED' && state.document_node_id) { accept(await getDocumentNode(state.document_node_id)); return }
        if (state.status !== 'RESERVED') { op.terminal = true; throw new Error('Sessione caricamento terminata o scaduta. Rimuovi il file e avvia una nuova operazione.') }
      }
      if (!op.uploaded) {
        patch(op, { status: 'uploading', error: undefined }, context)
        await uploadToPresignedPost(op.reservation, op.file, { signal: controller.signal, onProgress: progress => { if (current()) patch(op, { progress: progress.percentage }, context) } })
        if (!current()) return
        op.uploaded = true
      }
      patch(op, { status: 'completing', progress: 100 }, context)
      try { accept(await completeUploadSession(op.reservation.upload_id)) }
      catch {
        if (!current()) return
        patch(op, { status: 'verify', error: 'Verifica caricamento prima di riprovare.' }, context)
        try {
          const state = await getUploadSession(op.reservation.upload_id)
          if (!current()) return
          if (state.status === 'COMPLETED' && state.document_node_id) accept(await getDocumentNode(state.document_node_id))
          else if (state.status !== 'RESERVED') patch(op, { status: 'failed', terminal: true, error: 'Caricamento terminato o scaduto. Rimuovi il file e avvia una nuova operazione.' }, context)
        } catch { if (current()) patch(op, { status: 'verify', error: 'Impossibile verificare il caricamento. Riprova verifica.' }, context) }
      }
    } catch (e) { if (current()) patch(op, { status: op.uploaded && !op.terminal ? 'verify' : 'failed', error: (e as Error).message || 'Caricamento non riuscito.' }, context) }
    finally {
      if (context === generation.current) { controllers.current.delete(op.id); running.current.delete(op.id); pump.current() }
    }
  }
  pump.current = () => {
    for (const op of operations.current.values()) {
      if (running.current.size >= 2) return
      if (op.status !== 'queued' || op.removed || running.current.has(op.id)) continue
      running.current.add(op.id)
      void run(op, generation.current)
    }
  }
  const enqueue = (files: File[], destination: MailDocumentDestination) => {
    const pending = [...operations.current.values()].filter(item => !item.removed && !['completed', 'cancelled'].includes(item.status)).length
    if (latest.current.selectedCount + pending + files.length > 20) { setNotice('Massimo 20 allegati, inclusi i file ancora da caricare.'); return }
    for (const file of files) {
      const id = crypto.randomUUID()
      operations.current.set(id, { id, file, destination, key: crypto.randomUUID(), status: 'queued', progress: 0, uploaded: false, removed: false })
    }
    setNotice(''); publish(); pump.current()
  }
  const retry = (id: string) => {
    const op = operations.current.get(id)
    if (!op || op.terminal || running.current.has(id) || !['failed', 'verify'].includes(op.status)) return
    op.status = 'queued'; publish(); pump.current()
  }
  const remove = async (id: string) => {
    const op = operations.current.get(id)
    if (!op) return true
    const context = generation.current
    op.removed = true; controllers.current.get(id)?.abort(); publish()
    if (op.status === 'completed') { setNotice('Il documento completato rimane nei Documenti. Rimuovilo dalla selezione della mail se necessario.'); return true }
    if (op.reservation) {
      try {
        const state = await cancelUploadSession(op.reservation.upload_id)
        if (context === generation.current) {
          if (state.status === 'COMPLETED') setNotice('Il caricamento era già completato. Il documento rimane nei Documenti.')
          op.status = 'cancelled'
        }
      } catch { if (context === generation.current) { op.removed = false; op.status = 'failed'; op.error = 'Impossibile confermare l’annullamento. Riprova Rimuovi.'; publish() } return false }
    } else op.status = 'cancelled'
    return true
  }
  const pending = items.filter(item => !['completed', 'cancelled'].includes(item.status))
  return { items, notice, enqueue, retry, remove, pendingCount: pending.length, blocked: !!pending.length }
}
